import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  FormControl,
  FormControlLabel,
  FormLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Slider,
  Snackbar,
  Alert,
  TextField,
  Typography,
  LinearProgress,
  CircularProgress,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ChevronLeft } from "lucide-react";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import * as c from "../../../constants";
import { useTranslation } from "../../../language";
import ButtonForm from "../../../components/Button/ButtonForm";

const mappedPrefill = (val) => {
  if (!val) return "";
  if (val === "M") return "male";
  if (val === "F") return "female";
  return val;
};

/* kind: "speak" | "bioma" — selects which dynamic questionnaire schema/submit endpoint to use
   buttonOnly: render just the full-width open button (+ dialog) — caller provides the surrounding copy */
export default function QuestionnaireForm({ kind, nextStatus = 5, nextRoute = "/LabvisitStep", buttonOnly = false }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, language } = useTranslation();

  const [questionnaire, setQuestionnaire] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [current, setCurrent] = useState(0);
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const submitEndpoint =
    kind === "bioma" ? "/api/questionnaire-micro/submit/" : "/api/questionnaire/submit/";

  useEffect(() => {
    setLoading(true);
    axios.get(`${c.API_URL}/api/questionnaires/${kind}start_${language}/`).then((res) => {
      setQuestionnaire(res.data);
      setLoading(false);
    });
  }, [kind, language]);

  const questions = useMemo(() => {
    if (!questionnaire) return [];

    return questionnaire.questions.map((qq) => ({
      id: qq.question.id,
      key: qq.id,
      code: qq.question.code,
      text: qq.question.text,
      question_type: qq.question.question_type,
      options: qq.question.options || [],
      required: qq.required,
      order: qq.order,
      depends_on_code: qq.depends_on_code,
      depends_on_value: qq.depends_on_value,
      prefilled_value: qq.question.prefilled_value || null,
    }));
  }, [questionnaire]);

  useEffect(() => {
    if (!questionnaire) return;

    const initialAnswers = {};
    questionnaire.questions.forEach((qq) => {
      const code = qq.question.code;
      const mapped = mappedPrefill(qq.question.prefilled_value);
      if (mapped !== "") {
        initialAnswers[code] = mapped;
      }
    });

    setAnswers(initialAnswers);
  }, [questionnaire]);

  const handleChange = (code, value) => {
    setAnswers((prev) => ({ ...prev, [code]: value }));
  };

  const isQuestionVisible = (q) => {
    if (!q.depends_on_code) return true;

    const parentValue = answers[q.depends_on_code];
    if (!parentValue) return false;

    const parentValues = Array.isArray(parentValue) ? parentValue : [parentValue];
    const dependsValues = Array.isArray(q.depends_on_value) ? q.depends_on_value : [q.depends_on_value];

    return parentValues.some((val) => dependsValues.includes(val));
  };

  const visibleQuestions = questions.filter(isQuestionVisible);

  const isAnswered = (q) => {
    if (!q.required) return true;
    // the slider shows 1 by default and submit sends 1 when untouched
    if (q.question_type === "scale") return true;
    const value = answers[q.code];
    if (q.question_type === "multi") {
      return Array.isArray(value) && value.length > 0;
    }
    return value !== undefined && value !== null && value !== "";
  };

  const answeredCount = visibleQuestions.filter(isAnswered).length;
  const progress =
    visibleQuestions.length > 0 ? Math.round((answeredCount / visibleQuestions.length) * 100) : 0;

  const firstUnansweredIdx = visibleQuestions.findIndex((q) => !isAnswered(q));

  // conditional questions can disappear while answering — keep the index in range
  const currentIdx = Math.min(current, Math.max(visibleQuestions.length - 1, 0));
  const currentQuestion = visibleQuestions[currentIdx];
  const isLast = currentIdx === visibleQuestions.length - 1;

  const handleOpen = () => {
    setCurrent(firstUnansweredIdx === -1 ? 0 : firstUnansweredIdx);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (firstUnansweredIdx !== -1) {
      setCurrent(firstUnansweredIdx);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      await axios.post(`${c.API_URL}${submitEndpoint}`, {
        questionnaire: questionnaire.id,
        answers: visibleQuestions
          .map((q) => {
            let value = answers[q.code] ?? null;
            if (q.question_type === "boolean" && value !== null) {
              value = value === "true";
            }
            if (q.question_type === "scale" && value === null) {
              value = 1;
            }
            return { question: q.id, value };
          })
          .filter((a) => a.value !== null),
      });

      setShowSuccess(true);

      await axios.patch(`${c.API_URL}/api/onboarding/status/`, {
        onboarding_status: nextStatus,
      });
      setTimeout(() => {
        navigate(nextRoute);
      }, 1500);
    } catch (err) {
      const errData = err.response?.data;
      setSubmitError(errData?.detail || errData?.message || t("val_server_error"));
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (q, idx) => {
    const accentColor = idx % 2 === 0 ? theme.palette.tertiary.C300 : theme.palette.quartiary.C300;

    return (
    <Box
      key={q.key}
      sx={{
        border: "1px solid",
        borderRadius: 4,
        overflow: "hidden",
        borderLeft: `10px solid ${accentColor}`,
        p: 2,
        ...(theme.palette.mode === "light"
          ? { bgcolor: "grey.C50", borderColor: "grey.C900" }
          : {
              bgcolor: "greydark.C900",
              borderColor: "greydark.C400",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
            }),
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box
          sx={{
            flexShrink: 0,
            width: 28,
            height: 28,
            borderRadius: "50%",
            border: `2px solid ${accentColor}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mt: "2px",
          }}
        >
          <Typography
            variant="number4"
            sx={{ color: accentColor, fontWeight: 700, fontSize: "0.75rem", lineHeight: 1 }}
          >
            {idx + 1}
          </Typography>
        </Box>

        <FormControl fullWidth>
          <FormLabel>
            {q.text} {q.required && "*"}
          </FormLabel>

          {q.question_type === "text" && (
          <TextField
            multiline
            required={q.required}
            value={answers[q.code] || ""}
            onChange={(e) => handleChange(q.code, e.target.value)}
            sx={{ mt: 2, mb: 3 }}
          />
        )}

        {q.question_type === "number" && (
          <TextField
            type="number"
            value={answers[q.code] || ""}
            onChange={(e) => handleChange(q.code, Number(e.target.value))}
          />
        )}

        {q.question_type === "date" && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={answers[q.code] ? dayjs(answers[q.code], "YYYY-MM-DD", true) : null}
              onChange={(val) => handleChange(q.code, val ? val.format("YYYY-MM-DD") : "")}
              slotProps={{
                textField: { fullWidth: true, sx: { mt: 2, mb: 3 }, format: "YYYY-MM-DD" },
              }}
            />
          </LocalizationProvider>
        )}

        {q.question_type === "single" && (
          <RadioGroup
            value={answers[q.code] ? mappedPrefill(answers[q.code]) : mappedPrefill(q.prefilled_value)}
            onChange={(e) => handleChange(q.code, e.target.value)}
          >
            {q.options.map((opt) => (
              <FormControlLabel key={opt.value} value={opt.value} control={<Radio />} label={opt.label} />
            ))}
          </RadioGroup>
        )}

        {q.question_type === "multi" &&
          q.options.map((opt) => (
            <FormControlLabel
              key={opt.value}
              control={
                <Checkbox
                  checked={(answers[q.code] || []).includes(opt.value)}
                  onChange={(e) => {
                    const current = answers[q.code] || [];
                    handleChange(
                      q.code,
                      e.target.checked ? [...current, opt.value] : current.filter((v) => v !== opt.value)
                    );
                  }}
                />
              }
              label={opt.label}
            />
          ))}

        {q.question_type === "boolean" && (
          <RadioGroup
            value={answers[q.code] || ""}
            onChange={(e) => handleChange(q.code, e.target.value.toLowerCase())}
          >
            {q.options.map((opt) => (
              <FormControlLabel
                key={opt.id}
                value={String(opt.value).toLowerCase()}
                control={<Radio />}
                label={opt.label}
              />
            ))}

            {!q.required && (
              <FormControlLabel value="" control={<Radio />} label={t("onb_quest_no_answer")} />
            )}
          </RadioGroup>
        )}

        {q.question_type === "scale" && (
          <Slider
            min={1}
            max={10}
            step={1}
            marks
            value={answers[q.code] ?? 1}
            onChange={(_, value) => handleChange(q.code, value)}
            valueLabelDisplay="auto"
            sx={{
              mt: 3,
              "& .MuiSlider-valueLabel": { fontSize: "1.2rem", fontWeight: 600 },
              "& .MuiSlider-markLabel": { fontSize: "1rem" },
            }}
          />
        )}
        </FormControl>
      </Stack>
    </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={buttonOnly ? {} : { maxWidth: 600, mx: "auto", px: { xs: 2, sm: 4 } }}>
      {!buttonOnly && (
        <>
          <Typography variant="h6" textAlign="center" gutterBottom sx={{ mb: 1 }}>
            {t("onb_quest_form_title")}
          </Typography>

          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 4 }}>
            {t("onb_quest_form_subtitle")}
          </Typography>
        </>
      )}

      <ButtonForm name={t("onb_quest_open")} onClick={handleOpen} fullWidth={buttonOnly} />

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="sm"
        aria-labelledby="questionnaire-dialog-title"
        sx={{
          "& .MuiDialog-paper": {
            bgcolor: theme.palette.mode === "dark" ? theme.palette.background.paper : theme.palette.background.dialog,
            borderRadius: fullScreen ? 0 : "12px",
          },
        }}
      >
        <DialogTitle id="questionnaire-dialog-title" sx={{ pr: 7 }}>
          <Typography variant="h6" component="span" fontWeight={700}>
            {t("onb_quest_form_title")}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setDialogOpen(false)}
            sx={{ position: "absolute", right: 16, top: 12, color: theme.palette.text.secondary }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <LinearProgress variant="determinate" value={progress} sx={{ mb: 1, bgcolor: "tertiary.main" }} />
          <Typography variant="subtitle2" align="center" fontWeight={400} sx={{ mb: 3 }}>
            {answeredCount} / {visibleQuestions.length} {t("onb_quest_answered_label")}
          </Typography>

          {currentQuestion && renderQuestion(currentQuestion, currentIdx)}

          {submitError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {submitError}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: "100%" }}>
            <Button
              variant="text"
              startIcon={<ChevronLeft size={18} />}
              onClick={() => setCurrent(currentIdx - 1)}
              disabled={currentIdx === 0}
              sx={{ color: "text.primary" }}
            >
              {t("onb_quest_prev")}
            </Button>

            {/* ButtonForm brings its own top margin — neutralise it so it aligns with the back button */}
            <Box sx={{ "& > .MuiGrid-container": { mt: 0 } }}>
              {isLast ? (
                <ButtonForm
                  name={submitting ? <CircularProgress size={18} color="inherit" /> : t("onb_quest_submit")}
                  onClick={handleSubmit}
                  disabled={submitting}
                  sx={{ mt: 0 }}
                />
              ) : (
                <ButtonForm
                  name={t("onb_quest_next")}
                  onClick={() => setCurrent(currentIdx + 1)}
                  disabled={!currentQuestion || !isAnswered(currentQuestion)}
                  sx={{ mt: 0 }}
                />
              )}
            </Box>
          </Stack>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showSuccess}
        autoHideDuration={1500}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ top: "50%", transform: "translateY(-50%)" }}
      >
        <Alert severity="success">
          <Typography variant="h6">{t("onb_quest_submit_success")}</Typography>
        </Alert>
      </Snackbar>
    </Box>
  );
}
